import type {
  EntityType,
  ExtractionPattern,
  PropertySchema,
  RelationType,
  Schema,
} from '@/services/api/schemas'
import type { EntityFormState, SchemaGroup } from './types'

/** Temporal property types that drive timeline / conventions configuration. */
export const TEMPORAL_TYPES = new Set(['DATE', 'DATETIME', 'ZONED_DATETIME'])

/** Numeric property types that can stand in for inline lat/lon geo coords. */
export const GEO_NUMERIC_TYPES = new Set(['FLOAT', 'DECIMAL', 'NUMBER', 'DOUBLE'])

/**
 * Group schema rows by name, sorting versions newest-first. The group holding
 * the active schema sorts first (the global active singleton is the default a
 * user wants to open); the rest are alphabetical by name.
 */
export function groupSchemasByName(schemas: Schema[]): SchemaGroup[] {
  const map = new Map<string, Schema[]>()
  for (const s of schemas) {
    const list = map.get(s.name) ?? []
    list.push(s)
    map.set(s.name, list)
  }
  const groups: SchemaGroup[] = []
  for (const [name, versions] of map) {
    versions.sort((a, b) => b.version - a.version)
    groups.push({ name, versions })
  }
  const isActive = (g: SchemaGroup) => g.versions.some(v => v.is_active)
  groups.sort((a, b) => {
    const activeDelta = Number(isActive(b)) - Number(isActive(a))
    return activeDelta !== 0 ? activeDelta : a.name.localeCompare(b.name)
  })
  return groups
}

/**
 * The version a gallery card represents and opens: the active version when the
 * group has one, otherwise the latest (versions are sorted newest-first by
 * {@link groupSchemasByName}).
 */
export function representativeSchema(group: SchemaGroup): Schema {
  return group.versions.find(v => v.is_active) ?? group.versions[0]
}

/** Title-case a snake/kebab-cased property name for display labels. */
export function titleCase(name: string): string {
  return name
    .replace(/[-_]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

/**
 * True when the property set declares an inline lat/lon (or
 * latitude/longitude) pair of numeric coordinates — meaning the entity is a
 * fixed position and therefore cannot be trackable.
 */
export function hasInlineGeo(properties: PropertySchema[]): boolean {
  const numericByName = new Map<string, string>()
  for (const p of properties) {
    const t = (p.type || '').toUpperCase()
    if (GEO_NUMERIC_TYPES.has(t)) {
      numericByName.set((p.name || '').toLowerCase(), t)
    }
  }
  if (numericByName.has('lat') && numericByName.has('lon')) return true
  if (numericByName.has('latitude') && numericByName.has('longitude')) return true
  return false
}

/** The temporal subset of a property list (DATE / DATETIME / ZONED_DATETIME). */
export function temporalProperties(properties: PropertySchema[]): PropertySchema[] {
  return properties.filter(p => TEMPORAL_TYPES.has((p.type || '').toUpperCase()))
}

/** Case-insensitive substring match across an entity's label/description. */
export function filterEntities(entities: EntityType[], query: string): EntityType[] {
  const q = query.toLowerCase()
  return entities.filter(
    e => e.label.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q)
  )
}

/** Case-insensitive substring match across a relation's label/description. */
export function filterRelations(relations: RelationType[], query: string): RelationType[] {
  const q = query.toLowerCase()
  return relations.filter(
    r => r.label.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q)
  )
}

/** Case-insensitive substring match across a pattern's types/description. */
export function filterPatterns(patterns: ExtractionPattern[], query: string): ExtractionPattern[] {
  const q = query.toLowerCase()
  return patterns.filter(
    p =>
      p.source_type.toLowerCase().includes(q) ||
      p.relationship_type.toLowerCase().includes(q) ||
      p.target_type.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q)
  )
}

/**
 * After a property-set edit, drop timeline-exclude entries that no longer
 * reference a declared property name.
 */
export function pruneTimelineExclude(
  timelineExclude: string[],
  properties: PropertySchema[]
): string[] {
  const declared = new Set(properties.map(p => p.name))
  return timelineExclude.filter(n => declared.has(n))
}

/**
 * Seed the entity edit form from a persisted entity type. Legacy rows may have a
 * null/absent merge_strategy; fall back to the default `any` — what the engine
 * resolves an unset value to (ADR-0031) — so the dropdown is always a valid
 * {any, all} value.
 */
export function entityFormFromEntity(entity: EntityType): EntityFormState {
  return {
    label: entity.label,
    description: entity.description || '',
    properties: [...entity.properties],
    merge_strategy: entity.merge_strategy ?? 'any',
    trackable: entity.trackable ?? false,
    timeline_exclude: [...(entity.timeline_exclude ?? [])],
    display_label: { ...(entity.display_label ?? {}) },
  }
}

/**
 * Toggle the `is_display_name` marker on the property at `index`, with radio
 * semantics: marking one property clears the marker on every other (the backend
 * errors on more than one marker per entity type). Toggling the already-marked
 * property clears it entirely, leaving the deterministic name fallback chain in
 * charge. Returns a new array of new property objects — never mutates the input.
 */
export function toggleDisplayName(properties: PropertySchema[], index: number): PropertySchema[] {
  const turningOff = properties[index]?.is_display_name === true
  return properties.map((p, i) => ({ ...p, is_display_name: turningOff ? false : i === index }))
}

/**
 * After a property-set edit, drop display-label overrides whose property name
 * is no longer declared.
 */
export function pruneDisplayLabel(
  displayLabel: Record<string, string>,
  properties: PropertySchema[]
): Record<string, string> {
  const declared = new Set(properties.map(p => p.name))
  return Object.fromEntries(Object.entries(displayLabel).filter(([k]) => declared.has(k)))
}
